/**
 * Ad click IDs, carried in the URL through every navigation (RVZ-28582).
 *
 * A visitor who arrives from an ad keeps the platform's click ID in the URL as they
 * browse, so the platform's tag can tie later conversions back to the click. Only the
 * latest click per platform is kept, until it's older than the platform's attribution window.
 *
 * These methods are auto-imported in components and pages and can be used
 * directly in templates, <script setup> or anywhere in the Options API without
 * having to manually import them.
 */

import { filterQuery } from 'ufo'
import type { LocationQuery } from '#vue-router'
import { ONE_DAY_IN_SECONDS, ONE_SECOND_IN_MS } from '#shared/constants/durations'
import { CLICK_ID_MAX_AGE_IN_DAYS_BY_PLATFORM, CLICK_ID_PARAMETERS_BY_PLATFORM } from '~/constants/click-ids'
import type { ClickIdParameter, ClickIdPlatform, ClickIds, StoredClickIds } from '~/types/click-ids'

const CLICK_ID_PLATFORMS = Object.keys(CLICK_ID_PARAMETERS_BY_PLATFORM) as ClickIdPlatform[]
const CLICK_ID_PARAMETERS = new Set<string>(Object.values(CLICK_ID_PARAMETERS_BY_PLATFORM).flat())

/**
 * Click IDs are opaque tokens made of URL-safe characters. Anything else is
 * ignored, so that a hand-edited URL or cookie can't inject other content into links.
 */
const CLICK_ID_VALUE_PATTERN = /^[\w.-]{1,512}$/

function getClickIdValue(query: LocationQuery, parameter: ClickIdParameter) {
  // The query can come from a cookie, whose values aren't necessarily strings.
  const value: unknown = getQueryParameter(query, parameter)

  return typeof value === 'string' ? value.trim() : undefined
}

/**
 * Gets a platform's click ID from a query string: the first well-formed one in order
 * of precedence.
 *
 * @param query The query string, as an object.
 * @param platform The ad platform.
 * @returns The click ID parameter and value, or undefined when there is none.
 */
function getPlatformClickId(query: LocationQuery, platform: ClickIdPlatform) {
  for (const parameter of CLICK_ID_PARAMETERS_BY_PLATFORM[platform]) {
    const value = getClickIdValue(query, parameter)

    if (value && CLICK_ID_VALUE_PATTERN.test(value)) {
      return { parameter, value }
    }
  }

  return undefined
}

/**
 * Reads the click IDs from a query string, or from an untrusted stored value such as
 * a cookie, keeping one well-formed click ID per platform.
 *
 * @param query The query string, as an object.
 * @returns The click IDs, as query parameters.
 *
 * @example parseClickIds({ gclid: 'abc', utm_source: 'google' }) // { gclid: 'abc' }
 */
export function parseClickIds(query: unknown): ClickIds {
  const clickIds: ClickIds = {}

  if (!query || typeof query !== 'object') {
    return clickIds
  }

  for (const platform of CLICK_ID_PLATFORMS) {
    const clickId = getPlatformClickId(query as LocationQuery, platform)

    if (clickId) {
      clickIds[clickId.parameter] = clickId.value
    }
  }

  return clickIds
}

/**
 * Reads the click IDs like `parseClickIds`, but undefined when there are none.
 *
 * @param value The query string, as an object, or an untrusted stored value.
 * @returns The click IDs, or undefined when there are none.
 */
export function parseClickIdsIfAny(value: unknown): ClickIds | undefined {
  const clickIds = parseClickIds(value)

  return Object.keys(clickIds).length ? clickIds : undefined
}

/**
 * Merges newly seen click IDs into the stored ones: a new click replaces the stored
 * one for its platform, even when it's a different parameter (a gbraid replaces a gclid).
 *
 * @param stored The stored click IDs.
 * @param incoming The click IDs just seen.
 * @returns The merged click IDs.
 */
export function mergeLatestClickIds(stored: ClickIds, incoming: ClickIds): ClickIds {
  const merged: ClickIds = {}

  for (const platform of CLICK_ID_PLATFORMS) {
    const clickId = getPlatformClickId(incoming, platform) ?? getPlatformClickId(stored, platform)

    if (clickId) {
      merged[clickId.parameter] = clickId.value
    }
  }

  return merged
}

// A cookie written before first-seen times were recorded has none: its click IDs count as first seen now.
function getFirstSeenAt(cookie: unknown, parameter: ClickIdParameter, now: number) {
  const firstSeenAt: unknown = Object(Object(cookie).firstSeenAt)[parameter]

  return typeof firstSeenAt === 'number' ? firstSeenAt : now
}

/**
 * Records newly seen click IDs in the click ID cookie, like `mergeLatestClickIds`. A click
 * ID that is already stored keeps when it was first seen, so carrying it in the URL never
 * extends its life.
 *
 * @param cookie The click ID cookie's value, which is untrusted.
 * @param incoming The click IDs just seen.
 * @param now The current time, in ms since the epoch.
 * @returns The click ID cookie's new value.
 */
export function recordClickIds(cookie: unknown, incoming: ClickIds, now = Date.now()): StoredClickIds {
  const stored = parseClickIds(cookie)
  const clickIds = mergeLatestClickIds(stored, incoming)
  const firstSeenAt: StoredClickIds['firstSeenAt'] = {}

  for (const [parameter, value] of Object.entries(clickIds) as [ClickIdParameter, string][]) {
    firstSeenAt[parameter] = stored[parameter] === value ? getFirstSeenAt(cookie, parameter, now) : now
  }

  return { ...clickIds, firstSeenAt }
}

/**
 * Reads the click IDs in the click ID cookie that are still within their platform's
 * attribution window. Past it, a click can't earn the platform credit.
 *
 * @param cookie The click ID cookie's value, which is untrusted.
 * @param now The current time, in ms since the epoch.
 * @returns The click IDs, or undefined when there are none.
 */
export function parseLiveClickIds(cookie: unknown, now = Date.now()): ClickIds | undefined {
  const clickIds = parseClickIds(cookie)
  const live: ClickIds = {}

  for (const platform of CLICK_ID_PLATFORMS) {
    const clickId = getPlatformClickId(clickIds, platform)
    const maxAge = CLICK_ID_MAX_AGE_IN_DAYS_BY_PLATFORM[platform] * ONE_DAY_IN_SECONDS * ONE_SECOND_IN_MS

    if (clickId && now - getFirstSeenAt(cookie, clickId.parameter, now) <= maxAge) {
      live[clickId.parameter] = clickId.value
    }
  }

  return Object.keys(live).length ? live : undefined
}

/**
 * Finds the stored click IDs that a query string doesn't carry yet. A platform counts
 * as carried when the query has any of its click ID parameters, so a click ID that is
 * already in the URL is never replaced.
 *
 * @param query The query string, as an object.
 * @param stored The stored click IDs.
 * @returns The query parameters to add, or undefined when nothing is missing.
 */
export function findMissingClickIds(query: LocationQuery, stored: ClickIds): ClickIds | undefined {
  const missing: ClickIds = {}

  for (const platform of CLICK_ID_PLATFORMS) {
    const clickId = getPlatformClickId(stored, platform)
    const isCarried = CLICK_ID_PARAMETERS_BY_PLATFORM[platform].some((parameter) => getClickIdValue(query, parameter))

    if (clickId && !isCarried) {
      missing[clickId.parameter] = clickId.value
    }
  }

  return Object.keys(missing).length ? missing : undefined
}

/**
 * Gets where to redirect so that a URL carries the stored click IDs it's missing,
 * keeping its path, its other parameters and its hash. A click ID already in the URL
 * is never replaced.
 *
 * @param route The current route.
 * @param stored The stored click IDs, from an untrusted source such as a cookie or a booking.
 * @returns The route to redirect to, or undefined when the URL carries them all.
 *
 * @example getClickIdsRedirect({ path: '/a', query: {}, hash: '' }, { gclid: 'abc' }) // { path: '/a', query: { gclid: 'abc' }, hash: '' }
 */
export function getClickIdsRedirect(route: { path: string, query: LocationQuery, hash: string }, stored: unknown) {
  const missing = findMissingClickIds(route.query, parseClickIds(stored))

  if (!missing) {
    return undefined
  }

  return { path: route.path, query: { ...route.query, ...missing }, hash: route.hash }
}

/**
 * Removes click IDs from a URL or path, keeping every other parameter and the hash.
 * Used wherever a URL leaves the visitor's own browsing: share links and link previews.
 *
 * @param url An absolute URL, or a path with an optional query and hash.
 * @returns The URL without click IDs.
 *
 * @example stripClickIds('/rv-rental/abc?gclid=xyz&StartDate=2026-07-01') // '/rv-rental/abc?StartDate=2026-07-01'
 */
export function stripClickIds(url: string) {
  return filterQuery(url, (key) => !CLICK_ID_PARAMETERS.has(key))
}
